import { z } from 'zod';
import { billingCycleSchema, planKeySchema, planModuleSchema } from '../constants/plans.js';
import { subscriptionStatusSchema } from '../plans/entitlements.js';
import { resourceUsageSchema } from '../plans/quotas.js';

/**
 * What `/api/v1/subscription` answers, and what a plan change asks for (task 23).
 *
 * The response is deliberately fat. A billing screen that has to make four calls to say
 * "you are on Growth, using 4 of 10 seats, renewing on the 14th" will render three of those
 * facts before the fourth arrives, and the owner reads a half-finished sentence about money.
 *
 * Nothing in here takes payment — that is task 24. A plan change recorded through these
 * shapes is an intent the shop has expressed and the limits immediately obey; the card, the
 * proration and the failed-payment dance belong to the module that owns the gateway.
 */

/** Usage against one limit, as the screen draws it. `allowance: null` is unlimited. */
export const seatUsageSchema = z.object({
  used: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  allowance: z.number().int().positive().nullable(),
  remaining: z.number().int().nonnegative().nullable(),
  atLimit: z.boolean(),
  exceeded: z.boolean(),
});
export type SeatUsageView = z.infer<typeof seatUsageSchema>;

/**
 * Every meter the shop is measured by, in one object.
 *
 * Seats sit apart from the rest rather than as another row in `resources`, and that is not
 * tidiness: a seat is spent by an invitation nobody has accepted yet, which no generic
 * row-counter models. Folding it in would have meant either losing that rule or teaching the
 * generic counter about invitations, and the second is how a simple thing stops being one.
 */
export const planUsageSchema = z.object({
  seats: seatUsageSchema,
  /**
   * The counted allowances — customers, orders this month, catalogue items, stock items and
   * photo storage. Present so the billing screen draws every meter from the response it
   * already has, and so an in-module affordance ("Products 12 of 50") costs no extra call.
   */
  resources: z.array(resourceUsageSchema).default([]),
});
export type PlanUsage = z.infer<typeof planUsageSchema>;

/** One row of the comparison table. Prices are integer paise, always. */
export const planOptionSchema = z.object({
  key: planKeySchema,
  label: z.string(),
  monthlyPricePaise: z.number().int().nonnegative(),
  annualPricePaise: z.number().int().nonnegative(),
  staffSeats: z.number().int().positive().nullable(),
  modules: z.array(planModuleSchema),
  /** True for the plan the shop is on, so the table needs no second lookup to mark it. */
  current: z.boolean(),
  /** Blocked downgrades are shown, disabled, with the reason — never hidden. */
  available: z.boolean(),
  blockedReason: z.string().nullable(),
});
export type PlanOption = z.infer<typeof planOptionSchema>;

export const subscriptionSummarySchema = z.object({
  planKey: planKeySchema,
  planLabel: z.string(),
  billingCycle: billingCycleSchema,
  status: subscriptionStatusSchema,
  /** Modules the plan includes. The client hides what the API would refuse from this list. */
  modules: z.array(planModuleSchema),
  readOnly: z.boolean(),
  trialDaysRemaining: z.number().int().nonnegative().nullable(),
  graceDaysRemaining: z.number().int().nonnegative().nullable(),
  trialEndsAt: z.string().nullable(),
  /** The date the owner is actually asking about when they open this screen. */
  renewalDate: z.string().nullable(),
  usage: planUsageSchema,
  plans: z.array(planOptionSchema),
});
export type SubscriptionSummary = z.infer<typeof subscriptionSummarySchema>;

/** Choosing a tier. The cycle is optional: most changes keep the one already in force. */
export const changePlanSchema = z.object({
  planKey: planKeySchema,
  billingCycle: billingCycleSchema.optional(),
});
export type ChangePlanInput = z.infer<typeof changePlanSchema>;

/** What a plan change did, including the modules that just went read-only. */
export const planChangeResultSchema = z.object({
  subscription: subscriptionSummarySchema,
  modulesLost: z.array(planModuleSchema),
  modulesGained: z.array(planModuleSchema),
});
export type PlanChangeResult = z.infer<typeof planChangeResultSchema>;
